/*
  GiftGo destination system.

  - Persists the buyer's destination choice in localStorage.
  - Drives the bottom sheet picker (custom element below).
  - Keeps the header pill in sync.
  - Filters product grids by dest:* tags.
  - Renders the delivery promise line, including the live same day countdown,
    on every <delivery-promise> element.

  Vanilla JS, no dependencies. The Liquid in delivery-promise.liquid renders a
  correct no-JS fallback, so everything here is progressive enhancement.
*/
(function () {
  'use strict';

  var CONFIG = window.GiftGoDelivery || {};
  var STORAGE_KEY = CONFIG.storageKey || 'giftgo_destination';
  var DESTS = CONFIG.destinations || [];
  var EXPRESS = CONFIG.express || { min: 2, max: 4 };
  var STANDARD = CONFIG.standard || { min: 7, max: 14 };

  var DEST_BY_CODE = {};
  DESTS.forEach(function (d) {
    DEST_BY_CODE[d.code] = d;
  });

  var CITY = { us: 'US cities', ca: 'Canadian cities', uk: 'UK cities', de: 'German cities', ng: 'Nigerian cities' };
  var CODE_LABEL = { us: 'US', uk: 'UK', ca: 'CA', de: 'DE', ng: 'NG', anywhere: 'All' };

  var currentCode = null;

  /* ----------------------------------------------------------------- utils */
  function getSaved() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function save(code) {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      /* private mode, ignore */
    }
  }

  function cityPhrase(code) {
    return CITY[code] || 'cities';
  }

  function addDays(n) {
    var d = new Date();
    d.setDate(d.getDate() + n);
    return d;
  }

  function fmtWeekdayDate(d) {
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function fmtMonthDate(d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function parseCutoff(str) {
    var m = /^(\d{1,2}):(\d{2})$/.exec((str || '').trim());
    if (!m) return null;
    var h = parseInt(m[1], 10);
    var mi = parseInt(m[2], 10);
    if (h > 23 || mi > 59) return null;
    return { h: h, mi: mi };
  }

  function cutoffLabel(c) {
    var ap = c.h >= 12 ? 'pm' : 'am';
    var hh = c.h % 12;
    if (hh === 0) hh = 12;
    return hh + ':' + String(c.mi).padStart(2, '0') + ap;
  }

  function fmtCountdown(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    if (m > 0) return m + 'm ' + s + 's';
    return s + 's';
  }

  /* Clone a flag SVG from the rendered picker options, so we never duplicate
     the SVG markup outside Liquid. */
  function flagHTML(code) {
    var src = document.querySelector('#DestinationPicker [data-destination-select="' + code + '"] .destination-flag');
    return src ? src.innerHTML : '';
  }

  function setFlag(flagEl, code) {
    if (!flagEl) return;
    var html = flagHTML(code);
    if (html) {
      flagEl.innerHTML = html;
      flagEl.className = 'destination-flag destination-flag--' + code;
    }
  }

  /* --------------------------------------------------------------- filtering */
  function matchesDestination(rawTags, code) {
    var tags = (rawTags || '')
      .split(/\s+/)
      .filter(function (t) {
        return t.indexOf('dest:') === 0;
      });
    // Nigeria is a separate local market: a product only counts when it is
    // explicitly tagged dest:ng. Worldwide and untagged (abroad) items do not
    // appear there, so nothing shows until products are tagged for Nigeria.
    if (code === 'ng') return tags.indexOf('dest:ng') !== -1;
    if (tags.length === 0) return true; // untagged is treated as worldwide (abroad)
    var hasWorldwide = tags.indexOf('dest:worldwide') !== -1;
    if (code === 'anywhere') return hasWorldwide;
    return hasWorldwide || tags.indexOf('dest:' + code) !== -1;
  }

  // Is a category (its collection's product mix) available for this destination?
  // Mirrors matchesDestination but over the aggregated dest codes a category
  // carries (data-giftgo-cat-dest) plus whether it has any untagged product.
  function categoryAvailable(el, code) {
    var codes = (el.getAttribute('data-giftgo-cat-dest') || '')
      .split(/\s+/)
      .filter(Boolean);
    var untagged = el.getAttribute('data-giftgo-cat-untagged') === 'true';
    if (code === 'ng') return codes.indexOf('ng') !== -1;
    if (code === 'anywhere') return untagged || codes.indexOf('worldwide') !== -1;
    return untagged || codes.indexOf('worldwide') !== -1 || codes.indexOf(code) !== -1;
  }

  // Hide category entries (homepage strip pills and drawer categories) that have
  // no products for the chosen destination, and collapse a now-empty strip or
  // drawer pane. Keeps the drawer's active panel on a visible category.
  function filterCategories(code) {
    document.querySelectorAll('[data-giftgo-cat]').forEach(function (el) {
      el.classList.toggle('giftgo-cat-hidden', !categoryAvailable(el, code));
    });

    document.querySelectorAll('giftgo-cats').forEach(function (cats) {
      var firstVisible = null;
      cats.querySelectorAll('.gcat__tab').forEach(function (tab) {
        var li = tab.closest('[data-giftgo-cat]') || tab;
        var idx = tab.getAttribute('data-gcat-tab');
        var hidden = li.classList.contains('giftgo-cat-hidden');
        if (!hidden && firstVisible === null) firstVisible = idx;
      });
      // Activate the first still-visible category so the panel never shows a
      // hidden category's products.
      cats.querySelectorAll('.gcat__tab').forEach(function (tab) {
        tab.classList.toggle('is-active', tab.getAttribute('data-gcat-tab') === firstVisible);
      });
      cats.querySelectorAll('[data-gcat-view]').forEach(function (view) {
        var on = view.getAttribute('data-gcat-view') === firstVisible;
        view.classList.toggle('is-active', on);
        view.hidden = !on;
      });
      var panes = cats.querySelector('.gcat__panes');
      if (panes) panes.classList.toggle('giftgo-cat-hidden', firstVisible === null);
    });

    document.querySelectorAll('[data-giftgo-catstrip]').forEach(function (strip) {
      var pills = strip.querySelectorAll('[data-giftgo-cat]');
      var anyVisible = Array.prototype.some.call(pills, function (p) {
        return !p.classList.contains('giftgo-cat-hidden');
      });
      strip.classList.toggle('giftgo-catstrip-empty', !anyVisible);
    });
  }

  function filterGrids(code) {
    document.querySelectorAll('[data-giftgo-dest]').forEach(function (card) {
      var show = matchesDestination(card.getAttribute('data-giftgo-dest'), code);
      var target = card.closest('.grid__item') || card;
      target.classList.toggle('destination-hidden', !show);
    });
    hideEmptyRails(code);
    hideEmptySections(code);
    fixCollectionGrids(code);
  }

  // Oct 18 (g): one stylesheet rule for the generic empty-section hiding and the
  // collection-grid notice, injected once so no CSS asset has to change.
  function ensureFilterStyles() {
    if (document.getElementById('GiftGoDestFilterStyles')) return;
    var st = document.createElement('style');
    st.id = 'GiftGoDestFilterStyles';
    st.textContent =
      '.shopify-section.giftgo-section-empty{display:none!important}' +
      '.giftgo-grid-notice{margin:2rem 1.5rem;text-align:center;font-size:1.5rem;color:rgba(31,29,27,.75)}';
    document.head.appendChild(st);
  }

  function cardVisible(it, code) {
    return matchesDestination(it.getAttribute('data-giftgo-dest'), code);
  }

  // Generic: any homepage/page section inside <main> whose destination-tagged
  // cards are ALL filtered out for this destination is hidden as a whole
  // (heading, View all link and button included), whatever section type it is.
  // Collection product grids are left to fixCollectionGrids (they keep their
  // filters, count and pagination). Sections with no tagged cards are untouched.
  function hideEmptySections(code) {
    ensureFilterStyles();
    var main = document.querySelector('main') || document.getElementById('MainContent');
    if (!main) return;
    main.querySelectorAll('.shopify-section').forEach(function (sec) {
      if (sec.querySelector('#product-grid, .product-grid')) return;
      var items = sec.querySelectorAll('[data-giftgo-dest]');
      if (items.length === 0) {
        sec.classList.remove('giftgo-section-empty');
        return;
      }
      var anyVisible = Array.prototype.some.call(items, function (it) {
        return cardVisible(it, code);
      });
      sec.classList.toggle('giftgo-section-empty', !anyVisible);
    });
  }

  // Collection grids: hidden cards already collapse (display:none, so no gaps).
  // The server count ("67 products") and pagination describe the whole market
  // catalogue, so when this page hides cards we restate the count honestly for
  // the page and, if nothing on the page is left, show a short notice so the
  // grid is not just blank space above the pagination.
  function fixCollectionGrids(code) {
    ensureFilterStyles();
    var dest = DEST_BY_CODE[code];
    var label = dest ? dest.label : '';
    document.querySelectorAll('#product-grid, .product-grid').forEach(function (grid) {
      var items = grid.querySelectorAll('[data-giftgo-dest]');
      if (items.length === 0) return;
      var visible = Array.prototype.filter.call(items, function (it) {
        return cardVisible(it, code);
      }).length;
      var filtered = visible < items.length && code !== 'anywhere' && !!label;

      document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(function (el) {
        if (el.dataset.giftgoOrig === undefined) el.dataset.giftgoOrig = el.textContent;
        el.textContent = filtered
          ? visible + (visible === 1 ? ' product' : ' products') + ' on this page deliver to ' + label
          : el.dataset.giftgoOrig;
      });

      var notice = grid.parentNode.querySelector('.giftgo-grid-notice');
      if (filtered && visible === 0) {
        if (!notice) {
          notice = document.createElement('p');
          notice.className = 'giftgo-grid-notice';
          grid.parentNode.insertBefore(notice, grid.nextSibling);
        }
        var hasMore = !!document.querySelector('.pagination');
        notice.textContent =
          'Nothing on this page delivers to ' + label + '.' +
          (hasMore ? ' Try another page, or change your destination.' : ' Try changing your destination.');
        notice.hidden = false;
      } else if (notice) {
        notice.hidden = true;
      }
    });
  }

  // Hide an offers rail when none of its products ship to the chosen
  // destination, so we never show, say, a "Today's Delivery" rail with nothing
  // available for the United Kingdom. A homepage rail sits alone in its section,
  // so hiding it clears the whole strip; the cart upsell rail is nested inside
  // the cart drawer, so we hide only the rail element itself, never its section
  // (which would wrongly collapse the entire cart drawer). Only acts on tagged
  // rails: an untagged or unchosen destination leaves everything visible.
  function hideEmptyRails(code) {
    document.querySelectorAll('[data-giftgo-rail]').forEach(function (rail) {
      var items = rail.querySelectorAll('[data-giftgo-dest]');
      if (items.length === 0) return;
      var anyVisible = Array.prototype.some.call(items, function (it) {
        return matchesDestination(it.getAttribute('data-giftgo-dest'), code);
      });
      // Homepage rails own their section; the upsell is nested, so collapse
      // only itself.
      var target = rail.classList.contains('giftgo-rail')
        ? rail.closest('.shopify-section') || rail
        : rail;
      target.classList.toggle('giftgo-rail-empty', !anyVisible);
    });
  }

  /* ---------------------------------------------------------- promise render */
  function renderPromise(el, code) {
    var speed = el.dataset.speed || 'standard';
    var dest = DEST_BY_CODE[code];
    var line = el.querySelector('[data-promise-line]');
    var countdown = el.querySelector('[data-promise-countdown]');
    var cutoff = dest ? parseCutoff(dest.cutoff) : null;

    var effective = speed;
    // Oct 18: same-day never depends on a cutoff time, only on dest:* tags.
    var promiseTags = (el.dataset.destTags || '').split(/\s+/);
    if (speed === 'same-day' && code && promiseTags.indexOf('dest:' + code) === -1) effective = 'express';

    el.classList.remove(
      'delivery-promise--same-day',
      'delivery-promise--express',
      'delivery-promise--standard'
    );
    el.classList.add('delivery-promise--' + effective);

    // Keep the PDP destination header current.
    var nameEl = el.querySelector('[data-promise-dest-name]');
    if (nameEl && dest) nameEl.textContent = dest.label;
    var headFlag = el.querySelector('[data-promise-dest] .destination-flag');
    if (headFlag && dest) setFlag(headFlag, code);

    if (effective === 'same-day') {
      el._giftgoSameDay = { cutoff: cutoff, code: code };
      updateSameDay(el);
      return;
    }

    el._giftgoSameDay = null;
    if (countdown) countdown.hidden = true;

    if (speed === 'express') {
      line.textContent = 'Delivered in 2\u20135 days.';
    } else if (speed === 'standard') {
      line.textContent = 'Delivered in 7\u201315 working days (Mon\u2013Fri, excluding holidays).';
    } else if (effective === 'express') {
      // Same-day fallback where no cutoff exists: unchanged original wording.
      line.textContent = 'Delivered by ' + fmtWeekdayDate(addDays(EXPRESS.max)) + '.';
    } else {
      line.textContent =
        'Delivered between ' + fmtMonthDate(addDays(STANDARD.min)) + ' and ' + fmtMonthDate(addDays(STANDARD.max)) + '.';
    }
  }

  function updateSameDay(el) {
    var info = el._giftgoSameDay;
    if (!info) return;
    var line = el.querySelector('[data-promise-line]');
    var countdown = el.querySelector('[data-promise-countdown]');
    // Oct 18: same-day has no cutoff; one fixed line, no countdown.
    var sdTags = (el.dataset.destTags || '').split(/\s+/);
    if (line) line.textContent = sdTags.indexOf('dest:ca') !== -1
      ? 'Delivered today in all US and Canada cities.'
      : 'Delivered today in all US cities.';
    if (countdown) countdown.hidden = true;
  }

  function renderAllPromises(code) {
    document.querySelectorAll('delivery-promise').forEach(function (el) {
      renderPromise(el, code);
    });
  }

  // One global ticker keeps every same day countdown live.
  function startTicker() {
    setInterval(function () {
      document.querySelectorAll('delivery-promise').forEach(function (el) {
        if (el._giftgoSameDay) updateSameDay(el);
      });
    }, 1000);
  }

  /* --------------------------------------------------------------- the pill */
  function updatePill(code) {
    var dest = DEST_BY_CODE[code];
    document.querySelectorAll('[data-destination-pill]').forEach(function (pill) {
      var nameEl = pill.querySelector('[data-pill-name]');
      var codeEl = pill.querySelector('[data-pill-code]');
      var flagEl = pill.querySelector('[data-pill-flag] .destination-flag');
      if (dest) {
        if (nameEl) nameEl.textContent = dest.label;
        if (codeEl) codeEl.textContent = CODE_LABEL[code] || dest.label;
        setFlag(flagEl, code);
      }
    });
  }

  function markSelected() {
    document.querySelectorAll('[data-destination-select]').forEach(function (b) {
      // First-time visitors (nothing saved yet) see United States pre-selected.
      // Visual only: nothing is saved or applied until they tap a choice.
      var shown = currentCode || CONFIG.defaultCode || 'us';
      b.classList.toggle('is-selected', b.dataset.destinationSelect === shown);
    });
  }

  // Destination-aware line under the hero CTA.
  function updateHeroLine(code) {
    var dest = code ? DEST_BY_CODE[code] : null;
    var text;
    if (!dest) {
      text = 'Choose your destination to see delivery dates.';
    } else if (parseCutoff(dest.cutoff)) {
      text = 'Sending to ' + dest.label + '? Same day delivery available.';
    } else {
      text = 'Sending to ' + dest.label + '. Know the delivery date before you pay.';
    }
    document.querySelectorAll('[data-giftgo-hero-line]').forEach(function (el) {
      el.textContent = text;
    });
  }

  /* ------------------------------------------------------------- apply/select */
  function apply(code) {
    updatePill(code);
    renderAllPromises(code);
    filterGrids(code);
    filterCategories(code);
    markSelected();
    updateHeroLine(code);
    document.dispatchEvent(new CustomEvent('giftgo:destination-change', { detail: { code: code } }));
  }

  function select(code) {
    if (!DEST_BY_CODE[code]) return;
    currentCode = code;
    save(code);
    apply(code);
    syncMarket(code);
  }

  // "Within Nigeria" products are restricted to the Nigeria market in Shopify,
  // so choosing that destination must also switch the buyer's country context
  // to Nigeria, and leaving it must switch back to the primary market. Submits
  // the hidden localization form (a normal page reload) only when the country
  // actually needs to change; never runs on plain page load, so no loops.
  function syncMarket(code) {
    var form = document.getElementById('GiftGoLocalizationForm');
    if (!form) return;
    var input = form.querySelector('[name="country_code"]');
    if (!input) return;
    var current = (CONFIG.currentCountry || '').toUpperCase();
    var target = null;
    if (code === 'ng') {
      target = 'NG';
    } else if (current === 'NG') {
      target = (CONFIG.primaryCountry || 'US').toUpperCase();
    }
    if (!target || target === current) return;
    // The market switch is a full reload; without feedback the sheet looks
    // frozen for however long the network takes. Show the overlay immediately.
    var dest = DEST_BY_CODE[code];
    showSwitchOverlay(dest ? 'Switching to ' + dest.label : 'Switching your store');
    input.value = target;
    form.submit();
  }

  // Full-screen "switching" overlay with a spinner, styled inline so it works
  // before any stylesheet is guaranteed. Removed automatically if the page is
  // restored from the back-forward cache.
  function showSwitchOverlay(label) {
    if (document.getElementById('GiftGoSwitching')) return;
    var el = document.createElement('div');
    el.id = 'GiftGoSwitching';
    el.setAttribute('role', 'status');
    el.style.cssText =
      'position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(250,246,241,0.96);color:#1f1d1b;font-size:15px;font-weight:600;text-align:center;padding:24px;';
    var style = document.createElement('style');
    style.textContent = '@keyframes giftgo-spin{to{transform:rotate(360deg)}}';
    var spin = document.createElement('span');
    spin.style.cssText =
      'width:34px;height:34px;border-radius:50%;border:3px solid rgba(179,38,58,0.25);border-top-color:#b3263a;animation:giftgo-spin 0.8s linear infinite;';
    var txt = document.createElement('span');
    txt.textContent = label;
    el.appendChild(style);
    el.appendChild(spin);
    el.appendChild(txt);
    document.body.appendChild(el);
  }
  // Public hook for other components (cart drawer, dynamically loaded sections).
  window.GiftGo = window.GiftGo || {};
  window.GiftGo.getDestination = function () {
    return currentCode;
  };
  window.GiftGo.refresh = function () {
    apply(currentCode || 'anywhere');
  };
  window.GiftGo.openPicker = openPicker;

  function openPicker() {
    var picker = document.querySelector('destination-picker');
    if (picker && typeof picker.open === 'function') picker.open();
  }

  /* ----------------------------------------------------- the custom element */
  class DestinationPicker extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('.destination-picker__sheet');
      this.overlay = this.querySelector('[data-destination-overlay]');
      this.isOpen = false;

      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      var closeBtn = this.querySelector('[data-destination-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));

      this.querySelectorAll('[data-destination-select]').forEach(
        function (btn) {
          btn.addEventListener('click', function () {
            select(btn.dataset.destinationSelect);
            this.close();
          }.bind(this));
        }.bind(this)
      );

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && this.isOpen) this.close();
      }.bind(this));
    }

    open() {
      this.classList.add('destination-picker--open');
      document.documentElement.classList.add('destination-locked');
      this.isOpen = true;
      markSelected();
      if (this.sheet) this.sheet.focus();
    }

    close() {
      this.classList.remove('destination-picker--open');
      document.documentElement.classList.remove('destination-locked');
      this.isOpen = false;
    }
  }

  if (!customElements.get('destination-picker')) {
    customElements.define('destination-picker', DestinationPicker);
  }

  /* --------------------------------------------------------------------- init */
  function init() {
    // Delegate every "open the picker" trigger (header pill, change links).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-destination-open]');
      if (trigger) {
        e.preventDefault();
        openPicker();
      }
    });

    // If the browser restores this page from the back-forward cache after a
    // market switch, the switching overlay would still be covering it.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        var overlay = document.getElementById('GiftGoSwitching');
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }
    });

    var saved = getSaved();
    var country = (CONFIG.currentCountry || '').toUpperCase();

    // Keep the saved destination and the Shopify market context coherent.
    // If the page is being served in the Nigeria market (theme editor "View as"
    // or a returning NG session), the destination must be ng, or the client
    // filter would hide every Nigeria-only product the server just rendered.
    if (country === 'NG' && saved !== 'ng') {
      saved = 'ng';
      save('ng');
    }

    if (saved && DEST_BY_CODE[saved]) {
      currentCode = saved;
      apply(saved);
    } else {
      // No choice yet: show everything, render safe promises, ask once.
      currentCode = null;
      renderAllPromises('anywhere');
      updateHeroLine(null);
      if (CONFIG.autoOpen) {
        var picker = document.querySelector('destination-picker');
        if (picker && typeof picker.open === 'function') picker.open();
      }
    }

    // The reverse mismatch: destination says ng but the market context is not
    // Nigeria (e.g. the choice was made before market switching shipped). Move
    // into the Nigeria market now, at most once per session so blocked cookies
    // can never cause a reload loop.
    if (saved === 'ng' && country && country !== 'NG') {
      var attempted = false;
      try {
        attempted = sessionStorage.getItem('giftgo_ng_synced') === '1';
        if (!attempted) sessionStorage.setItem('giftgo_ng_synced', '1');
      } catch (e) {
        attempted = true; // no sessionStorage: do not risk a loop
      }
      if (!attempted) syncMarket('ng');
    }

    startTicker();

    // Re-apply after Shopify loads/reloads a section (pagination, theme editor).
    document.addEventListener('shopify:section:load', function () {
      apply(currentCode || 'anywhere');
    });

    // Re-render promises when the cart drawer re-renders its line items, so the
    // promise + badge follow each item right up to checkout (the last surface
    // we control on this plan). Dawn publishes 'cart-update' via pubsub.js.
    if (typeof subscribe === 'function') {
      subscribe('cart-update', function () {
        requestAnimationFrame(function () {
          renderAllPromises(currentCode || 'anywhere');
          // The cart drawer re-renders its contents (including the upsell row)
          // on every update, so re-apply destination filtering to the fresh DOM.
          if (currentCode) {
            filterGrids(currentCode);
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
